import { ref, unref, watch, onMounted, onBeforeUnmount } from 'vue';
import * as Sentry from '@sentry/vue';
import {
  lastDeliveryAt,
  lastTransportChangeAt,
  recordDeadChannel,
} from 'dashboard/helper/realtimeDelivery';

// REFETCH DE SEGURANÇA — detecta e cura canal realtime MORTO.
//
// O ActionCable só reage a queda de transporte (actionCable.js →
// ReconnectService). O modo de falha que mais custa é outro: o socket fica
// aberto, os pings chegam e NENHUM broadcast é entregue (watchdog.rb). A tela
// congela num passado que parece presente e só F5 conserta.
//
// A cada ~45 s (com jitter, só com a aba visível) busca uma ASSINATURA barata
// do dado (contagem + maior atividade) e compara com a da conferência
// anterior:
//   - não mudou                          → nada a dizer;
//   - mudou E o canal entregou algo dessa
//     família desde a conferência anterior → a mudança tem dono, saudável;
//   - mudou e o canal NÃO entregou nada   → SUSPEITA (ainda sem veredito);
//   - numa conferência ao menos ~1 intervalo depois da que levantou a
//     suspeita, o canal continua sem entregar nada dessa família desde a
//     base da suspeita                      → canal morto: cura (onStale →
//     rebusca) e DENUNCIA (contador + Sentry uma vez por sessão).
//
// Por que duas conferências: o broadcast sai pelo ActionCableBroadcastJob
// (Sidekiq). Fila atrasada = o dado já está no banco e o evento ainda não
// saiu, e isso acontece em TODAS as abas ao mesmo tempo — com veredito numa
// conferência só, um pico de fila virava "canal morto" em massa no Sentry. A
// confirmação custa ~45 s a mais pra curar o canal morto de verdade.
// A suspeita guarda QUANDO nasceu (`at`): conferência fora do timer (volta à
// aba, troca de escopo) segundos depois NÃO confirma — a fila não teve tempo
// de andar. Antes do intervalo mínimo (intervalo − jitter) a suspeita só
// espera.
// simplificação: atraso de fila MAIOR que um intervalo (~45 s + carência)
// continua lido como canal morto — do lado do browser não dá pra separar
// "o job ainda não rodou" de "o socket não entrega". Upgrade = o servidor
// carimbar na assinatura o instante do último broadcast ENFILEIRADO da
// família, e o detector comparar com o último entregue.
//
// A suspeita é da FAMÍLIA, não do escopo: `since` é comparado com a última
// entrega de qualquer evento da família (realtimeDelivery é por família).
// Trocar de conversa/filtro dá base nova pra assinatura mas MANTÉM a suspeita
// — senão o atendente que troca de conversa a cada minuto nunca confirmaria.
//
// Volta da suspensão do notebook: os timers ficam congelados, o socket morre
// e o ReconnectService só percebe depois da primeira conferência. A suspensão
// é medida pelo atraso do PRÓPRIO timer: previsto × real, descontado o tempo
// com a aba oculta. Atrasou mais que SAFETY_REFETCH_SUSPEND_FACTOR × intervalo
// = a página ficou congelada: trata como troca de transporte (base nova,
// suspeita descartada, sem veredito). Medir pelo timer — e não pela distância
// entre conferências — faz rodada que falhou (rede) não parecer suspensão.
// Aba só oculta não conta — o socket segue entregando em segundo plano, e a
// volta pra aba é justamente quando a conferência mais vale.
// simplificação: suspensão COM a aba oculta conta como tempo oculto (o
// relógio não diz qual dos dois foi); quem segura esse caso é a confirmação
// acima — até a 2ª conferência o ReconnectService já registrou a troca.
//
// Curar sem detectar seria pior que o estado atual: a falha viraria invisível.

export const SAFETY_REFETCH_INTERVAL_MS = 45_000;
export const SAFETY_REFETCH_JITTER = 0.2; // ±20%
// Evento em voo: o dado já mudou no banco quando a assinatura foi lida, mas o
// broadcast ainda não chegou. Espera um pouco antes do veredito.
export const SAFETY_REFETCH_GRACE_MS = 5_000;
// Timer atrasado além disto (× intervalo, já descontado o tempo oculto) = a
// página esteve congelada (suspensão). Atraso normal de timer é de ms.
export const SAFETY_REFETCH_SUSPEND_FACTOR = 1;

// Um relato por família por sessão (carga da página): o detector roda a cada
// 45 s em cada aba e repetiria o mesmo evento.
const reportedFamilies = new Set();

const reportDeadChannel = (family, count) => {
  if (reportedFamilies.has(family)) return;
  reportedFamilies.add(family);
  // Sem PII: só a família e o contador — nada de id de conversa ou conteúdo.
  Sentry.captureMessage('realtime: canal morto detectado pelo refetch', {
    level: 'warning',
    tags: { realtime_family: family },
    extra: { dead_channel_count: count },
  });
};

const nextDelay = intervalMs =>
  intervalMs *
  (1 - SAFETY_REFETCH_JITTER + Math.random() * 2 * SAFETY_REFETCH_JITTER);

const wait = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const isVisible = () => document.visibilityState === 'visible';

/**
 * @param {Object} options
 * @param {string} options.family - família de evento (REALTIME_FAMILIES)
 * @param {Ref|string|null} options.key - identidade do escopo (filtros,
 *   conversa aberta). Mudou → a próxima assinatura vira a nova base, sem
 *   veredito (suspeita pendente da família é mantida). `null` desliga a
 *   conferência (sem escopo, nada a conferir).
 * @param {Function} options.fetchSignature - async, devolve algo serializável
 * @param {Function} options.onStale - chamado com `{ family, since }` ao
 *   detectar canal morto; `since` é a base da suspeita (última conferência
 *   antes da mudança sem evento — pode ser de outro escopo da mesma família)
 */
export function useSafetyRefetch({
  family,
  key,
  fetchSignature,
  onStale,
  intervalMs = SAFETY_REFETCH_INTERVAL_MS,
  graceMs = SAFETY_REFETCH_GRACE_MS,
}) {
  const staleCount = ref(0);
  const lastCheckedAt = ref(null);
  // Intervalo mínimo entre a conferência que levanta a suspeita e a que pode
  // confirmá-la: o menor intervalo que o timer sorteia.
  const confirmAfterMs = intervalMs * (1 - SAFETY_REFETCH_JITTER);

  let baseline = null; // { key, signature, checkedAt }
  // Mudança sem evento esperando confirmação: `since` = base da suspeita,
  // `at` = início da conferência que a levantou.
  let suspect = null;
  // Tempo acumulado com a aba oculta (desconta do atraso do timer).
  let hiddenTotal = 0;
  let hiddenSince = null;
  let timer = null;
  // Quando o timer pendente deveria disparar, e o tempo oculto naquele
  // instante do agendamento — é o que mede a suspensão.
  let timerDue = null; // { at, hiddenAt }
  // Suspensão detectada e ainda não absorvida por uma conferência que deu
  // certo: a próxima assinatura vira base, sem veredito.
  let rebasePending = false;
  let inFlight = false;
  let inFlightScope = null;
  let rerun = false;
  let active = false;

  const deliveredSince = since =>
    (lastDeliveryAt(family) ?? -Infinity) > since ||
    (lastTransportChangeAt() ?? -Infinity) > since;

  const hiddenNow = now =>
    hiddenTotal + (hiddenSince === null ? 0 : now - hiddenSince);

  // Chamado no disparo do timer (antes de reagendar) e no início de toda
  // conferência — a conferência da volta à aba pode rodar antes do timer
  // atrasado disparar.
  const detectSuspension = now => {
    if (!timerDue) return;
    const late = now - timerDue.at - (hiddenNow(now) - timerDue.hiddenAt);
    if (late <= SAFETY_REFETCH_SUSPEND_FACTOR * intervalMs) return;
    rebasePending = true;
    timerDue = null; // atraso já contado; o próximo agendamento mede de novo
  };

  const declareDead = async since => {
    suspect = null;
    staleCount.value += 1;
    const total = recordDeadChannel(family);
    reportDeadChannel(family, total);
    await onStale({ family, since });
  };

  const check = async () => {
    const scope = unref(key);
    if (scope === null || scope === undefined || !isVisible()) return;
    if (inFlight) {
      // Escopo novo durante uma conferência: roda de novo quando ela acabar,
      // pra base do escopo novo não esperar 45 s. Mesmo escopo: já coberto.
      if (scope !== inFlightScope) rerun = true;
      return;
    }
    inFlight = true;
    inFlightScope = scope;
    try {
      const startedAt = Date.now();
      detectSuspension(startedAt);
      let signature;
      try {
        signature = JSON.stringify(await fetchSignature());
      } catch {
        // Falha de rede/servidor: esta rodada não tem veredito (não dá pra
        // afirmar nem que mudou nem que não mudou) e a base fica como estava —
        // a próxima conferência compara contra ela. Suspensão detectada aqui
        // segue pendente pra próxima.
        return;
      }
      // O escopo mudou durante a busca: a assinatura é do escopo velho (o
      // watch do escopo já pediu a conferência do novo). Desmontou durante a
      // busca: ninguém mais olha essa tela, nada de base nem veredito.
      if (!active || unref(key) !== scope) return;

      const previous = baseline;
      baseline = { key: scope, signature, checkedAt: startedAt };
      lastCheckedAt.value = startedAt;
      if (rebasePending) {
        rebasePending = false;
        suspect = null;
        return;
      }

      if (suspect) {
        if (deliveredSince(suspect.since)) {
          suspect = null; // chegou atrasado: canal vivo, avalia esta rodada
        } else if (startedAt - suspect.at >= confirmAfterMs) {
          // Conferência de confirmação: o evento atrasado (fila do Sidekiq)
          // teve um intervalo inteiro pra chegar. Não chegou → morto.
          await declareDead(suspect.since);
          return;
        } else {
          // Cedo demais (volta à aba, troca de escopo): a fila ainda não teve
          // tempo de andar. Mantém a suspeita; esta rodada já é base.
          return;
        }
      }

      // Escopo novo: base nova sem veredito (a suspeita, se houver, segue —
      // é da família, não do escopo).
      if (!previous || previous.key !== scope) return;
      if (previous.signature === signature) return;
      if (deliveredSince(previous.checkedAt)) return;

      await wait(graceMs);
      if (!active || deliveredSince(previous.checkedAt)) return;

      suspect = { since: previous.checkedAt, at: startedAt };
    } finally {
      inFlight = false;
      if (rerun && active) {
        rerun = false;
        check();
      }
    }
  };

  const schedule = () => {
    clearTimeout(timer);
    const delay = nextDelay(intervalMs);
    const now = Date.now();
    timerDue = { at: now + delay, hiddenAt: hiddenNow(now) };
    timer = setTimeout(() => {
      detectSuspension(Date.now());
      // Aba oculta não confere: ninguém vê a tela e a conferência custa
      // requisição. O que ficou para trás é conferido na volta.
      // Reagenda antes de conferir: se a cura (onStale) lançar, o erro sobe
      // visível e o laço continua vivo.
      if (active) schedule();
      if (isVisible()) check();
    }, delay);
  };

  // Voltar pra aba é quando o usuário mais acredita no que vê — e quando a
  // tela pode estar mais velha. Confere na hora (sem confirmar suspeita
  // recente: ver `confirmAfterMs`).
  const onVisibilityChange = () => {
    const now = Date.now();
    if (isVisible()) {
      if (hiddenSince !== null) hiddenTotal += now - hiddenSince;
      hiddenSince = null;
      check();
    } else if (hiddenSince === null) {
      hiddenSince = now;
    }
  };

  onMounted(() => {
    active = true;
    if (!isVisible()) hiddenSince = Date.now();
    document.addEventListener('visibilitychange', onVisibilityChange);
    check();
    schedule();
  });

  onBeforeUnmount(() => {
    active = false;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  // Escopo novo → base nova já, sem esperar 45 s pra começar a valer.
  watch(
    () => unref(key),
    () => {
      if (active) check();
    }
  );

  return { staleCount, lastCheckedAt, check };
}

// Só pros specs.
export const resetSafetyRefetchReports = () => reportedFamilies.clear();
